import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Collapse,
  Divider,
  Paper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AddCircleOutline,
  Assessment,
  Inventory,
  Build,
  Groups,
  CalendarMonth,
  MenuBook,
  Construction,
  ExpandMore,
  ExpandLess,
  InfoOutlined,
  DeleteOutline,
} from "@mui/icons-material";
import { useParams, useNavigate, Route, Routes } from "react-router-dom";
import PageWithHeader from "../common/PageWithHeader";
import {
  useGetComsBlockStatsQuery,
  useGetComsBlockEvmOverviewQuery,
  useUpdateComsBlockMutation,
  ComsEvmOverview,
} from "../../redux/comsApi";
import DailyReports from "./DailyReports";
import WorkDone from "./WorkDone";
import MaterialsBudget from "./MaterialsBudget";
import LabourSummary from "./LabourSummary";
import EquipmentSummary from "./EquipmentSummary";
import MonthlyReport from "./MonthlyReport";
import MonthlyReportInstanceDetail from "./MonthlyReportInstanceDetail";
import WeeklyReports from "./WeeklyReports";
import WeeklyReportDetail from "./WeeklyReportDetail";
import Catalog from "./Catalog";
import DailyReportEditor from "./DailyReportEditor";

const COMS_NAVY = "#123456";

interface TabInfo {
  label: string;
  path: string;
  icon: React.ReactElement;
}

const TABS: TabInfo[] = [
  { label: "Overview", path: "", icon: <Assessment /> },
  { label: "Daily Reports", path: "daily-reports", icon: <Assessment /> },
  { label: "Work Done", path: "work-done", icon: <Build /> },
  { label: "Materials", path: "materials", icon: <Inventory /> },
  { label: "Labour", path: "labour", icon: <Groups /> },
  { label: "Equipment", path: "equipment", icon: <Construction /> },
  { label: "Monthly Report", path: "monthly-report", icon: <CalendarMonth /> },
  { label: "Weekly Reports", path: "weekly-reports", icon: <CalendarMonth /> },
  { label: "Catalog", path: "catalog", icon: <MenuBook /> },
];

const blockPath = (blockId: string, sub = "") =>
  sub
    ? `/coms/daily-site-report/blocks/${blockId}/${sub}`
    : `/coms/daily-site-report/blocks/${blockId}`;

const fmt = (v?: number | null) =>
  `$${(v ?? 0).toLocaleString("en-ZA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
const fmtN = (v?: number | null, dp = 2) => (v ?? 0).toFixed(dp);

type EditableFloatingLabourEntry = {
  month: string;
  amount: string;
  note: string;
};

type EditablePettyCashEntry = {
  month: string;
  amount: string;
  note: string;
};

/** Tooltip-annotated metric box for the EVM panel */
const EvmMetric: React.FC<{
  label: string;
  abbr: string;
  value: string;
  color?: string;
  help: string;
}> = ({ label, abbr, value, color = COMS_NAVY, help }) => (
  <Box
    sx={{
      flex: "1 1 150px",
      px: 2.5,
      py: 2,
      textAlign: "center",
      borderRight: "1px solid #f0f0f0",
      "&:last-child": { borderRight: "none" },
    }}
  >
    <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
      <Typography variant="body2" color="text.secondary">
        {abbr}
      </Typography>
      <Tooltip title={`${label}: ${help}`} arrow>
        <InfoOutlined sx={{ fontSize: 14, color: "#aaa", cursor: "help" }} />
      </Tooltip>
    </Box>
    <Typography sx={{ fontWeight: 700, color, fontSize: "1.1rem", mt: 0.25 }}>
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

const BlockOverview: React.FC<{ blockId: string }> = ({ blockId }) => {
  const navigate = useNavigate();
  const { data: stats, isLoading } = useGetComsBlockStatsQuery(blockId);
  const {
    data: evm,
    isLoading: evmLoading,
    refetch: refetchEvm,
  } = useGetComsBlockEvmOverviewQuery(blockId);
  const [updateBlock] = useUpdateComsBlockMutation();

  // Opening balance edit state
  const [balanceOpen, setBalanceOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openingCost, setOpeningCost] = useState("");
  const [openingCostMat, setOpeningCostMat] = useState("");
  const [openingCostLab, setOpeningCostLab] = useState("");
  const [openingCostEquip, setOpeningCostEquip] = useState("");
  const [openingCostDate, setOpeningCostDate] = useState("");
  const [openingPL, setOpeningPL] = useState("");
  const [startingProfitBaseline, setStartingProfitBaseline] = useState("");
  const [floatingLabourEntries, setFloatingLabourEntries] = useState<
    EditableFloatingLabourEntry[]
  >([]);
  const [pettyCashEntries, setPettyCashEntries] = useState<
    EditablePettyCashEntry[]
  >([]);

  // Initialise edits from live evm data
  React.useEffect(() => {
    if (!evm) return;
    setOpeningCost(
      evm.openingActualCost > 0 ? String(evm.openingActualCost) : "",
    );
    setOpeningCostMat(
      evm.openingActualCostMaterials > 0
        ? String(evm.openingActualCostMaterials)
        : "",
    );
    setOpeningCostLab(
      evm.openingActualCostLabour > 0
        ? String(evm.openingActualCostLabour)
        : "",
    );
    setOpeningCostEquip(
      evm.openingActualCostEquipment > 0
        ? String(evm.openingActualCostEquipment)
        : "",
    );
    setOpeningCostDate(evm.openingActualCostDate ?? "");
    setOpeningPL(
      evm.openingProfitLoss !== 0 ? String(evm.openingProfitLoss) : "",
    );
    setStartingProfitBaseline(
      evm.profitStartingBaseline !== 0
        ? String(evm.profitStartingBaseline)
        : "",
    );
    setFloatingLabourEntries(
      (evm.floatingLabourEntries?.length
        ? evm.floatingLabourEntries
        : evm.floatingLabourAllocation > 0 || evm.floatingLabourNote
        ? [
            {
              month: "",
              amount: evm.floatingLabourAllocation,
              note: evm.floatingLabourNote ?? "",
            },
          ]
        : []
      ).map((entry) => ({
        month: entry.month ?? "",
        amount: entry.amount ? String(entry.amount) : "",
        note: entry.note ?? "",
      })),
    );
    setPettyCashEntries(
      (evm.pettyCashEntries?.length
        ? evm.pettyCashEntries
        : evm.pettyCashAllocation > 0 || evm.pettyCashNote
        ? [
            {
              month: "",
              amount: evm.pettyCashAllocation,
              note: evm.pettyCashNote ?? "",
            },
          ]
        : []
      ).map((entry) => ({
        month: entry.month ?? "",
        amount: entry.amount ? String(entry.amount) : "",
        note: entry.note ?? "",
      })),
    );
  }, [evm]);

  const updateFloatingEntry = (
    index: number,
    field: keyof EditableFloatingLabourEntry,
    value: string,
  ) => {
    setFloatingLabourEntries((prev) =>
      prev.map((entry, entryIndex) =>
        entryIndex === index ? { ...entry, [field]: value } : entry,
      ),
    );
  };

  const addFloatingEntry = () => {
    setFloatingLabourEntries((prev) => [
      ...prev,
      { month: "", amount: "", note: "" },
    ]);
  };

  const removeFloatingEntry = (index: number) => {
    setFloatingLabourEntries((prev) =>
      prev.filter((_, entryIndex) => entryIndex !== index),
    );
  };

  const updatePettyCashEntry = (
    index: number,
    field: keyof EditablePettyCashEntry,
    value: string,
  ) => {
    setPettyCashEntries((prev) =>
      prev.map((entry, entryIndex) =>
        entryIndex === index ? { ...entry, [field]: value } : entry,
      ),
    );
  };

  const addPettyCashEntry = () => {
    setPettyCashEntries((prev) => [
      ...prev,
      { month: "", amount: "", note: "" },
    ]);
  };

  const removePettyCashEntry = (index: number) => {
    setPettyCashEntries((prev) =>
      prev.filter((_, entryIndex) => entryIndex !== index),
    );
  };

  const handleSaveBalances = async () => {
    setSaving(true);
    try {
      const cleanedFloatingEntries = floatingLabourEntries
        .map((entry) => ({
          month: entry.month || undefined,
          amount: Number(entry.amount) || 0,
          note: entry.note.trim() || undefined,
        }))
        .filter((entry) => entry.month || entry.amount || entry.note);

      const cleanedPettyCashEntries = pettyCashEntries
        .map((entry) => ({
          month: entry.month || undefined,
          amount: Number(entry.amount) || 0,
          note: entry.note.trim() || undefined,
        }))
        .filter((entry) => entry.month || entry.amount || entry.note);

      await updateBlock({
        id: blockId,
        data: {
          openingActualCost: Number(openingCost) || 0,
          openingActualCostMaterials: Number(openingCostMat) || 0,
          openingActualCostLabour: Number(openingCostLab) || 0,
          openingActualCostEquipment: Number(openingCostEquip) || 0,
          openingActualCostDate: openingCostDate || undefined,
          openingProfitLoss: Number(openingPL) || 0,
          profitStartingBaseline: Number(startingProfitBaseline) || 0,
          floatingLabourAllocation: cleanedFloatingEntries.reduce(
            (sum, entry) => sum + (entry.amount || 0),
            0,
          ),
          floatingLabourNote:
            cleanedFloatingEntries
              .map((entry) => entry.note)
              .filter(Boolean)
              .join(" | ") || undefined,
          floatingLabourEntries: cleanedFloatingEntries,
          pettyCashAllocation: cleanedPettyCashEntries.reduce(
            (sum, entry) => sum + (entry.amount || 0),
            0,
          ),
          pettyCashNote:
            cleanedPettyCashEntries
              .map((entry) => entry.note)
              .filter(Boolean)
              .join(" | ") || undefined,
          pettyCashEntries: cleanedPettyCashEntries,
        },
      }).unwrap();
      await refetchEvm();
      setBalanceOpen(false);
    } finally {
      setSaving(false);
    }
  };

  if (isLoading)
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  if (!stats) return null;

  const statCards = [
    { label: "Reports Filed", value: stats.reportsCount },
    { label: "Catalog Activities", value: stats.activitiesCount },
    { label: "Catalog Materials", value: stats.materialsCount },
    { label: "Total Catalog Items", value: stats.catalogTotal },
  ];

  const navCards = [
    {
      label: "Daily Reports",
      sub: "Add or view daily reports",
      path: "daily-reports",
      icon: <Assessment sx={{ fontSize: 48, color: COMS_NAVY }} />,
    },
    {
      label: "Work Done",
      sub: "Cumulative work to date",
      path: "work-done",
      icon: <Build sx={{ fontSize: 48, color: COMS_NAVY }} />,
    },
    {
      label: "Materials",
      sub: "Cumulative materials used",
      path: "materials",
      icon: <Inventory sx={{ fontSize: 48, color: COMS_NAVY }} />,
    },
    {
      label: "Labour",
      sub: "Per-day labour records",
      path: "labour",
      icon: <Groups sx={{ fontSize: 48, color: COMS_NAVY }} />,
    },
    {
      label: "Equipment",
      sub: "Cumulative equipment usage",
      path: "equipment",
      icon: <Construction sx={{ fontSize: 48, color: COMS_NAVY }} />,
    },
    {
      label: "Monthly Report",
      sub: "Generate range summary",
      path: "monthly-report",
      icon: <CalendarMonth sx={{ fontSize: 48, color: COMS_NAVY }} />,
    },
    {
      label: "Weekly Reports",
      sub: "Auto-generated weekly summaries",
      path: "weekly-reports",
      icon: <CalendarMonth sx={{ fontSize: 48, color: COMS_NAVY }} />,
    },
    {
      label: "Catalog",
      sub: "Manage activities & materials",
      path: "catalog",
      icon: <MenuBook sx={{ fontSize: 48, color: COMS_NAVY }} />,
    },
  ];

  return (
    <Box>
      {/* Stat summary */}
      <Box display="flex" gap={3} mb={4} flexWrap="wrap">
        {statCards.map((s) => (
          <Box
            key={s.label}
            sx={{
              flex: "1 1 140px",
              bgcolor: "#fff",
              borderRadius: 2,
              p: 3,
              boxShadow: "0 1px 8px rgba(18,52,86,0.1)",
              textAlign: "center",
            }}
          >
            <Typography variant="h2" sx={{ color: COMS_NAVY, fontWeight: 700 }}>
              {s.value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {s.label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* ── EVM Panel (Part B) ──────────────────────────────────────────────── */}
      <Paper sx={{ mb: 4, borderRadius: 2, overflow: "hidden" }}>
        <Box
          sx={{
            bgcolor: COMS_NAVY,
            px: 3,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography sx={{ color: "#fff", fontWeight: 700 }}>
            Cost Performance (EVM) — Cumulative to Date
          </Typography>
          <Button
            size="small"
            onClick={() => setBalanceOpen((o) => !o)}
            sx={{
              color: "#fff",
              borderColor: "rgba(255,255,255,0.4)",
              "&:hover": { borderColor: "#fff" },
            }}
            variant="outlined"
            endIcon={balanceOpen ? <ExpandLess /> : <ExpandMore />}
          >
            {balanceOpen ? "Hide Settings" : "Edit Opening Balances"}
          </Button>
        </Box>

        {evmLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : evm ? (
          <>
            {/* 8 EVM metrics */}
            <Box
              display="flex"
              flexWrap="wrap"
              borderBottom="1px solid #f0f0f0"
            >
              <EvmMetric
                abbr="BAC"
                label="Budget at Completion"
                value={fmt(evm.bac)}
                help="The total planned budget for this block."
              />
              <EvmMetric
                abbr="EV"
                label="Earned Value"
                value={fmt(evm.ev)}
                help="The value of the work actually completed so far, valued at planned rates."
              />
              <EvmMetric
                abbr="AC"
                label="Actual Cost"
                value={fmt(evm.ac)}
                help="The real money spent so far using overview tab totals: opening actual cost, materials cost to date, labour, equipment, and floating labour."
              />
              <EvmMetric
                abbr="CV"
                label="Cost Variance"
                value={fmt(evm.cv)}
                color={(evm.cv ?? 0) >= 0 ? "#2e7d32" : "#c62828"}
                help="Earned value minus actual cost. Positive = under budget; negative = over budget."
              />
              <EvmMetric
                abbr="CPI"
                label="Cost Performance Index"
                value={fmtN(evm.cpi)}
                color={
                  (evm.cpi ?? 1) >= 1
                    ? "#2e7d32"
                    : (evm.cpi ?? 1) >= 0.9
                    ? "#f57c00"
                    : "#c62828"
                }
                help="How efficiently money is being spent. Above 1 = under budget; below 1 = overspending."
              />
              <EvmMetric
                abbr="EAC"
                label="Estimate at Completion"
                value={fmt(evm.eac)}
                help="The projected total cost to finish this block if current spending efficiency continues."
              />
              <EvmMetric
                abbr="VAC"
                label="Variance at Completion"
                value={fmt(evm.vac)}
                color={(evm.vac ?? 0) >= 0 ? "#2e7d32" : "#c62828"}
                help="Budget at Completion minus Estimate at Completion. Positive = under budget; negative = over budget."
              />
              <EvmMetric
                abbr="TCPI"
                label="To-Complete Performance Index"
                value={fmtN(evm.tcpi)}
                color={(evm.tcpi ?? 1) <= 1 ? "#2e7d32" : "#c62828"}
                help="The cost efficiency needed on remaining work to still finish on budget."
              />
            </Box>

            {/* AC breakdown */}
            <Box
              sx={{
                px: 3,
                py: 1.5,
                bgcolor: "#f9fafb",
                display: "flex",
                flexWrap: "wrap",
                gap: 3,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                <strong>AC breakdown:</strong> Opening Actual Cost{" "}
                {fmt(evm.openingActualCost)}
                {evm.openingActualCostDate
                  ? ` (as at ${evm.openingActualCostDate})`
                  : ""}
                {` · Materials ${fmt(evm.materialsCostToDate)} · Labour ${fmt(
                  evm.labourCostToDate,
                )} · Equipment ${fmt(evm.equipmentCostToDate)}`}
                {evm.floatingLabourAllocation > 0 && (
                  <>
                    {" "}
                    · <strong>Floating Labour Allocation</strong>{" "}
                    {fmt(evm.floatingLabourAllocation)}
                    <Tooltip
                      title="These monthly floating labour entries are manually allocated and added to Actual Cost."
                      arrow
                    >
                      <InfoOutlined
                        sx={{
                          fontSize: 13,
                          ml: 0.5,
                          cursor: "help",
                          verticalAlign: "middle",
                        }}
                      />
                    </Tooltip>
                  </>
                )}
                {evm.pettyCashAllocation > 0 && (
                  <>
                    {" "}
                    · <strong>Petty Cash Allocation</strong>{" "}
                    {fmt(evm.pettyCashAllocation)}
                    <Tooltip
                      title="These monthly petty cash entries are manually allocated and added to Actual Cost."
                      arrow
                    >
                      <InfoOutlined
                        sx={{
                          fontSize: 13,
                          ml: 0.5,
                          cursor: "help",
                          verticalAlign: "middle",
                        }}
                      />
                    </Tooltip>
                  </>
                )}
              </Typography>
              {evm.floatingLabourEntries?.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  <strong>Monthly floating labour:</strong>{" "}
                  {evm.floatingLabourEntries
                    .map((entry) => {
                      const monthLabel = entry.month || "Unscheduled";
                      const note = entry.note ? ` — ${entry.note}` : "";
                      return `${monthLabel}: ${fmt(entry.amount)}${note}`;
                    })
                    .join(" | ")}
                </Typography>
              )}
              {evm.pettyCashEntries?.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  <strong>Monthly petty cash:</strong>{" "}
                  {evm.pettyCashEntries
                    .map((entry) => {
                      const monthLabel = entry.month || "Unscheduled";
                      const note = entry.note ? ` — ${entry.note}` : "";
                      return `${monthLabel}: ${fmt(entry.amount)}${note}`;
                    })
                    .join(" | ")}
                </Typography>
              )}
              {evm.openingProfitLoss !== 0 && (
                <Typography variant="caption" color="text.secondary">
                  <strong>Opening P/L balance:</strong>{" "}
                  {fmt(evm.openingProfitLoss)}
                </Typography>
              )}
            </Box>

            <Box sx={{ px: 3, pb: 2.25 }}>
              <Paper
                variant="outlined"
                sx={{
                  mt: 1.5,
                  p: 1.5,
                  borderColor: "#d6dbe8",
                  bgcolor: "#fbfcff",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, color: COMS_NAVY, mb: 0.5 }}
                >
                  EVM Explanation
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ display: "block", color: "#5c6373" }}
                >
                  EV is {fmt(evm.ev)}. This means you have earned work value of
                  {" "}{fmt(evm.ev)} against the planned budget.
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ display: "block", color: "#5c6373", mt: 0.4 }}
                >
                  AC is {fmt(evm.ac)}. This is the total money spent so far.
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ display: "block", color: "#5c6373", mt: 0.4 }}
                >
                  CPI is {fmtN(evm.cpi)}. This means for every $1.00 spent, you
                  are earning about ${fmtN(evm.cpi)} of planned value.
                  {" "}
                  {(evm.cpi ?? 1) > 1
                    ? "You are currently cost-efficient (under budget performance)."
                    : (evm.cpi ?? 1) === 1
                    ? "You are exactly on budget efficiency."
                    : "You are spending faster than value earned (cost pressure)."}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ display: "block", color: "#5c6373", mt: 0.4 }}
                >
                  EAC is {fmt(evm.eac)}. If current performance continues, this
                  is the expected final cost at completion.
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ display: "block", color: "#5c6373", mt: 0.4 }}
                >
                  VAC is {fmt(evm.vac)}.{" "}
                  {(evm.vac ?? 0) >= 0
                    ? `This projects a saving of ${fmt(Math.abs(evm.vac ?? 0))} against budget.`
                    : `This projects an overrun of ${fmt(Math.abs(evm.vac ?? 0))} against budget.`}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ display: "block", color: "#5c6373", mt: 0.4 }}
                >
                  TCPI is {fmtN(evm.tcpi)}.{" "}
                  {(evm.tcpi ?? 1) > 1
                    ? "Remaining work must be delivered at higher efficiency than current baseline to still hit BAC."
                    : "Current efficiency is generally sufficient to meet BAC on remaining work."}
                </Typography>
              </Paper>
            </Box>
          </>
        ) : null}

        {/* Opening Balances editor */}
        <Collapse in={balanceOpen}>
          <Divider />
          <Box sx={{ px: 3, py: 2.5, bgcolor: "#fafbfc" }}>
            <Typography variant="subtitle2" sx={{ mb: 2, color: COMS_NAVY }}>
              Opening Balances — enter pre-COMS historical figures here
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
              <TextField
                label="Opening Actual Cost (total)"
                size="small"
                type="number"
                value={openingCost}
                onChange={(e) => setOpeningCost(e.target.value)}
                sx={{ width: 220 }}
              />
              <TextField
                label="As-of Date"
                size="small"
                type="date"
                value={openingCostDate}
                onChange={(e) => setOpeningCostDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 180 }}
              />
              <TextField
                label="Opening P/L Balance"
                size="small"
                type="number"
                helperText="Pre-COMS profit/loss baseline"
                value={openingPL}
                onChange={(e) => setOpeningPL(e.target.value)}
                sx={{ width: 220 }}
              />
              <TextField
                label="Starting Profit Baseline"
                size="small"
                type="number"
                helperText="Used for cumulative profit from June reports"
                value={startingProfitBaseline}
                onChange={(e) => setStartingProfitBaseline(e.target.value)}
                sx={{ width: 240 }}
              />
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mb={1}
            >
              Optional cost breakdown (Materials / Labour / Equipment — for
              reference only, the total above is used in AC):
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
              <TextField
                label="Materials portion"
                size="small"
                type="number"
                value={openingCostMat}
                onChange={(e) => setOpeningCostMat(e.target.value)}
                sx={{ width: 180 }}
              />
              <TextField
                label="Labour portion"
                size="small"
                type="number"
                value={openingCostLab}
                onChange={(e) => setOpeningCostLab(e.target.value)}
                sx={{ width: 180 }}
              />
              <TextField
                label="Equipment portion"
                size="small"
                type="number"
                value={openingCostEquip}
                onChange={(e) => setOpeningCostEquip(e.target.value)}
                sx={{ width: 180 }}
              />
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mb={1}
            >
              Floating/Shared Labour Allocation — add monthly entries for this
              block&apos;s share of shared supervisors and unassigned labour:
            </Typography>
            {floatingLabourEntries.map((entry, index) => (
              <Box key={`${index}-${entry.month}`} display="flex" flexWrap="wrap" gap={2} mb={2} alignItems="center">
                <TextField
                  label="Month"
                  size="small"
                  type="month"
                  value={entry.month}
                  onChange={(e) => updateFloatingEntry(index, "month", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 180 }}
                />
                <TextField
                  label="Floating Labour Amount"
                  size="small"
                  type="number"
                  value={entry.amount}
                  onChange={(e) => updateFloatingEntry(index, "amount", e.target.value)}
                  sx={{ width: 220 }}
                />
                <TextField
                  label="Note (who entered, when, context)"
                  size="small"
                  value={entry.note}
                  onChange={(e) => updateFloatingEntry(index, "note", e.target.value)}
                  sx={{ flex: 1, minWidth: 260 }}
                />
                <Button
                  color="error"
                  startIcon={<DeleteOutline />}
                  onClick={() => removeFloatingEntry(index)}
                >
                  Remove
                </Button>
              </Box>
            ))}
            <Button
              variant="outlined"
              startIcon={<AddCircleOutline />}
              onClick={addFloatingEntry}
              sx={{ mb: 2, borderColor: COMS_NAVY, color: COMS_NAVY }}
            >
              Add Monthly Floating Labour
            </Button>
            {floatingLabourEntries.some((entry) => Number(entry.amount) > 0) && (
              <Alert severity="info" sx={{ mb: 2 }} icon={<InfoOutlined />}>
                Total floating labour of{" "}
                <strong>
                  {fmt(
                    floatingLabourEntries.reduce(
                      (sum, entry) => sum + (Number(entry.amount) || 0),
                      0,
                    ),
                  )}
                </strong>{" "}
                will be added to this block&apos;s Actual Cost from the monthly
                entries above.
              </Alert>
            )}
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mb={1}
            >
              Petty Cash Allocation — add monthly split amounts to include petty
              cash in Actual Cost:
            </Typography>
            {pettyCashEntries.map((entry, index) => (
              <Box
                key={`${index}-${entry.month}`}
                display="flex"
                flexWrap="wrap"
                gap={2}
                mb={2}
                alignItems="center"
              >
                <TextField
                  label="Month"
                  size="small"
                  type="month"
                  value={entry.month}
                  onChange={(e) =>
                    updatePettyCashEntry(index, "month", e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 180 }}
                />
                <TextField
                  label="Petty Cash Amount"
                  size="small"
                  type="number"
                  value={entry.amount}
                  onChange={(e) =>
                    updatePettyCashEntry(index, "amount", e.target.value)
                  }
                  sx={{ width: 220 }}
                />
                <TextField
                  label="Note (who entered, when, context)"
                  size="small"
                  value={entry.note}
                  onChange={(e) =>
                    updatePettyCashEntry(index, "note", e.target.value)
                  }
                  sx={{ flex: 1, minWidth: 260 }}
                />
                <Button
                  color="error"
                  startIcon={<DeleteOutline />}
                  onClick={() => removePettyCashEntry(index)}
                >
                  Remove
                </Button>
              </Box>
            ))}
            <Button
              variant="outlined"
              startIcon={<AddCircleOutline />}
              onClick={addPettyCashEntry}
              sx={{ mb: 2, borderColor: COMS_NAVY, color: COMS_NAVY }}
            >
              Add Monthly Petty Cash
            </Button>
            {pettyCashEntries.some((entry) => Number(entry.amount) > 0) && (
              <Alert severity="info" sx={{ mb: 2 }} icon={<InfoOutlined />}>
                Total petty cash of{" "}
                <strong>
                  {fmt(
                    pettyCashEntries.reduce(
                      (sum, entry) => sum + (Number(entry.amount) || 0),
                      0,
                    ),
                  )}
                </strong>{" "}
                will be added to this block&apos;s Actual Cost from the monthly
                entries above.
              </Alert>
            )}
            <Button
              variant="contained"
              disabled={saving}
              onClick={handleSaveBalances}
              sx={{ bgcolor: COMS_NAVY, "&:hover": { bgcolor: "#1a4a7a" } }}
            >
              {saving ? "Saving…" : "Save Opening Balances"}
            </Button>
          </Box>
        </Collapse>
      </Paper>

      {/* Nav cards */}
      <Box display="flex" flexWrap="wrap" gap={3}>
        {navCards.map((c) => (
          <Card
            key={c.path}
            sx={{
              width: 200,
              borderRadius: 3,
              boxShadow: "0 1px 8px rgba(18,52,86,0.1)",
              transition: "0.2s",
              "&:hover": {
                boxShadow: "0 4px 16px rgba(18,52,86,0.2)",
                transform: "translateY(-2px)",
              },
            }}
          >
            <CardActionArea
              onClick={() => navigate(blockPath(blockId, c.path))}
              sx={{ p: 2, textAlign: "center" }}
            >
              <CardContent>
                {c.icon}
                <Typography
                  variant="subtitle1"
                  sx={{ mt: 1, color: COMS_NAVY }}
                >
                  {c.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {c.sub}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

const BlockDashboard: React.FC = () => {
  const { blockId } = useParams<{ blockId: string }>();
  const navigate = useNavigate();
  const { data: stats } = useGetComsBlockStatsQuery(blockId ?? "", {
    skip: !blockId,
  });

  if (!blockId) return null;

  const block = stats?.block;

  return (
    <PageWithHeader>
      {/* ── Block header ────────────────────────────────────────────────── */}
      <Box sx={{ bgcolor: COMS_NAVY, color: "#fff", px: 5, py: 3 }}>
        <Typography
          variant="body2"
          sx={{ color: "rgba(255,255,255,0.6)", cursor: "pointer", mb: 0.5 }}
          onClick={() => navigate("/coms/daily-site-report")}
        >
          ← All blocks
        </Typography>
        <Typography variant="h1" sx={{ color: "#fff", fontWeight: 700 }}>
          {block ? `${block.code} — ${block.name}` : "…"}
        </Typography>
      </Box>

      {/* ── Tabs ────────────────────────────────────────────────────────── */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #e0e0e0", px: 3 }}>
        <Tabs
          variant="scrollable"
          scrollButtons="auto"
          value={false}
          sx={{
            "& .MuiTab-root": { textTransform: "none", fontWeight: 500 },
            "& .Mui-selected": { color: `${COMS_NAVY} !important` },
            "& .MuiTabs-indicator": { bgcolor: COMS_NAVY },
          }}
        >
          {TABS.map((t) => (
            <Tab
              key={t.path}
              label={t.label}
              onClick={() => navigate(blockPath(blockId, t.path))}
            />
          ))}
        </Tabs>
      </Box>

      {/* ── Tab content ─────────────────────────────────────────────────── */}
      <Box px={5} py={4}>
        <Routes>
          <Route index element={<BlockOverview blockId={blockId} />} />
          <Route
            path="daily-reports"
            element={<DailyReports blockId={blockId} />}
          />
          <Route
            path="daily-reports/:reportId"
            element={<DailyReportEditor blockId={blockId} />}
          />
          <Route path="work-done" element={<WorkDone blockId={blockId} />} />
          <Route
            path="materials"
            element={<MaterialsBudget blockId={blockId} />}
          />
          <Route path="labour" element={<LabourSummary blockId={blockId} />} />
          <Route
            path="equipment"
            element={<EquipmentSummary blockId={blockId} />}
          />
          <Route
            path="monthly-report"
            element={<MonthlyReport blockId={blockId} />}
          />
          <Route
            path="monthly-report/:instanceId"
            element={<MonthlyReportInstanceDetail blockId={blockId} />}
          />
          <Route
            path="weekly-reports"
            element={<WeeklyReports blockId={blockId} />}
          />
          <Route
            path="weekly-reports/:reportId"
            element={<WeeklyReportDetail blockId={blockId} />}
          />
          <Route path="catalog" element={<Catalog blockId={blockId} />} />
        </Routes>
      </Box>
    </PageWithHeader>
  );
};

export default BlockDashboard;
